import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import SecurityBanner from './components/SecurityBanner';
import ExpirationCountdown from './components/ExpirationCountdown';
import DesignGallery from './components/DesignGallery';
import ClientActions from './components/ClientActions';
import ProjectInfo from './components/ProjectInfo';
import TrustIndicators from './components/TrustIndicators';

const ClientPreview = () => {
  const { previewId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [isExpired, setIsExpired] = useState(false);

  // Get additional parameters from URL
  const clientName = searchParams?.get('client') || 'Sarah Johnson';
  const projectType = searchParams?.get('type') || 'Custom Diamond Engagement Ring';

  // Mock data - in real app this would come from API
  const mockProject = {
    id: previewId || "proj_001",
    title: projectType,
    description: "Elegant solitaire setting with 2-carat round brilliant diamond",
    clientName: clientName,
    jewelerName: "Luxury Jewels Studio",
    createdAt: "Dec 8, 2024",
    expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000)?.toISOString(), // 24 hours from now
    message: `Dear ${clientName},\n\nWe're excited to share the custom design concepts for your ${projectType?.toLowerCase()}. These renderings showcase the elegant design we discussed, featuring premium materials and craftsmanship.\n\nThe design emphasizes timeless elegance while ensuring maximum visual impact. Please review the different angles and let us know your thoughts.\n\nBest regards,\nMichael Chen\nSenior Designer`
  };

  const mockDesigns = [
    {
      id: "design_001",
      title: "Front View - Solitaire Setting",
      description: "Classic six-prong setting showcasing the diamond\'s brilliance",
      type: "image",
      url: "https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=800&h=600&fit=crop",
      thumbnail: "https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=200&h=150&fit=crop",
      createdAt: "Dec 8, 2024"
    },
    {
      id: "design_002", 
      title: "Side Profile View",
      description: "Elegant profile showing the ring\'s proportions and band design",
      type: "image",
      url: "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=800&h=600&fit=crop",
      thumbnail: "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=200&h=150&fit=crop",
      createdAt: "Dec 8, 2024"
    },
    {
      id: "design_003",
      title: "360° Rotation Video",
      description: "Complete view of the ring from all angles",
      type: "video",
      url: "https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4",
      thumbnail: "https://images.unsplash.com/photo-1617038260897-41a1f14a8ca0?w=200&h=150&fit=crop",
      createdAt: "Dec 8, 2024"
    },
    {
      id: "design_004",
      title: "Technical CAD Drawing",
      description: "Detailed technical specifications and measurements",
      type: "cad",
      url: "/assets/technical-drawing.pdf",
      createdAt: "Dec 8, 2024"
    }
  ];

  useEffect(() => {
    // Simulate API call
    const loadPreview = async () => {
      setIsLoading(true);
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Check if preview exists and is valid - now more flexible
      if (!previewId && !searchParams?.get('client')) {
        // If no previewId or client parameter, show default preview
        setProject({
          ...mockProject,
          id: "preview_001",
          clientName: "Sample Client",
          title: "Sample Project Preview"
        });
      } else {
        // Load project with provided parameters
        setProject(mockProject);
      }

      // Check if expired
      const now = new Date()?.getTime();
      const expiry = new Date(mockProject.expiresAt)?.getTime();
      setIsExpired(now > expiry);

      setDesigns(mockDesigns);
      setIsLoading(false);
    };

    loadPreview();

    // Prevent screenshots and right-click
    const preventScreenshot = (e) => {
      if (e?.key === 'PrintScreen' || (e?.ctrlKey && e?.shiftKey && e?.key === 'I') || 
          (e?.ctrlKey && e?.shiftKey && e?.key === 'J') || (e?.ctrlKey && e?.key === 'u')) {
        e?.preventDefault();
        return false;
      }
    };

    const preventRightClick = (e) => {
      e?.preventDefault();
      return false;
    };

    document.addEventListener('keydown', preventScreenshot);
    document.addEventListener('contextmenu', preventRightClick);

    return () => {
      document.removeEventListener('keydown', preventScreenshot);
      document.removeEventListener('contextmenu', preventRightClick);
    };
  }, [previewId, searchParams, navigate]);

  const handleApprove = async () => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Show success message
    alert('Thank you! Your approval has been sent to the jeweler. You will receive confirmation shortly.');
  };

  const handleReject = async (feedback) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Show success message
    alert('Thank you for your feedback! The jeweler will review your comments and get back to you with revised designs.');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-muted-foreground">Loading secure preview...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-heading text-foreground">Preview Not Found</h1>
          <p className="text-muted-foreground">This preview link is invalid or has been removed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SecurityBanner />
      <div className="pt-16 pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-8 space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-heading text-foreground mb-2">
                  Exclusive Design Preview
                </h1>
                <p className="text-muted-foreground">
                  Your custom jewelry design awaits your review
                </p>
              </div>
              <ExpirationCountdown expiresAt={project?.expiresAt} />
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Design Gallery */}
            <div className="lg:col-span-2 space-y-8">
              <DesignGallery designs={designs} />
              <ClientActions 
                onApprove={handleApprove}
                onReject={handleReject}
                isExpired={isExpired}
              />
            </div>

            {/* Right Column - Project Info & Trust */}
            <div className="space-y-6">
              <ProjectInfo project={project} />
              <TrustIndicators />
            </div>
          </div>
        </div>
      </div>
      {/* Footer */}
      <footer className="border-t border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center space-x-3">
              <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                <span className="text-accent-foreground font-bold text-sm">JT</span>
              </div>
              <span className="text-lg font-heading text-foreground">JewelThread</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Secure design collaboration for luxury jewelry
            </p>
            <p className="text-xs text-muted-foreground">
              © {new Date()?.getFullYear()} JewelThread. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClientPreview;